import React, { useState, useMemo } from 'react';
import Editor from '@monaco-editor/react';
import { Tab } from '../../stores';
import { cn, formatBytes, formatDuration, getStatusColor } from '../../lib/utils';
import { Copy, Check, ChevronDown, ChevronRight } from 'lucide-react';
import { useMonacoTheme } from '../../hooks';

interface ResponseViewerProps {
  tab: Tab;
}

export function ResponseViewer({ tab }: ResponseViewerProps) {
  const [activeSection, setActiveSection] = useState<'body' | 'headers'>('body');
  const [copied, setCopied] = useState(false);
  const [prettyPrint, setPrettyPrint] = useState(true);
  const monacoTheme = useMonacoTheme();

  const response = tab.response;

  const formattedBody = useMemo(() => {
    if (!response?.body) return '';

    if (!prettyPrint) return response.body;

    // Try to parse and format JSON
    try {
      const parsed = JSON.parse(response.body);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return response.body;
    }
  }, [response?.body, prettyPrint]);

  // Detect language from content-type header for syntax highlighting
  const responseLanguage = useMemo(() => {
    if (!response) return 'plaintext';

    const contentType = response.headers?.['content-type'] || response.headers?.['Content-Type'] || '';
    if (contentType.includes('json')) return 'json';
    if (contentType.includes('xml')) return 'xml';
    if (contentType.includes('html')) return 'html';
    if (contentType.includes('javascript')) return 'javascript';
    if (contentType.includes('css')) return 'css';

    // Try to detect JSON by parsing if content-type is not set
    if (response.body) {
      try {
        JSON.parse(response.body);
        return 'json';
      } catch {
        // Not JSON
      }
    }

    return 'plaintext';
  }, [response]);

  const handleCopy = async () => {
    if (!formattedBody) return;

    await navigator.clipboard.writeText(formattedBody);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getStatusBadgeColor = (status: number) => {
    if (status >= 200 && status < 300) return 'bg-green-500/10 text-green-500 border-green-500/20';
    if (status >= 300 && status < 400) return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
    if (status >= 400 && status < 500) return 'bg-orange-500/10 text-orange-500 border-orange-500/20';
    if (status >= 500) return 'bg-red-500/10 text-red-500 border-red-500/20';
    return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
  };

  if (tab.isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-muted/30">
        <div className="text-center">
          <div className="relative w-12 h-12 mx-auto mb-3">
            <div className="absolute inset-0 border-4 border-primary/20 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-transparent border-t-primary rounded-full animate-spin"></div>
          </div>
          <p className="text-muted-foreground">Sending request...</p>
        </div>
      </div>
    );
  }

  if (!response) {
    return (
      <div className="h-full flex items-center justify-center bg-muted/30">
        <div className="text-center text-muted-foreground">
          <p className="mb-1">No response yet</p>
          <p className="text-sm">Send a request to see the response here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Response Meta */}
      <div className="flex items-center gap-4 p-3 border-b border-border">
        {/* Status */}
        <div
          className={cn(
            'px-2.5 py-1 rounded-md text-sm font-medium border',
            getStatusBadgeColor(response.status)
          )}
        >
          {response.status} {response.statusText}
        </div>

        {/* Time */}
        <div className="text-sm text-muted-foreground">
          <span className="text-foreground font-medium">{formatDuration(response.time)}</span>
        </div>

        {/* Size */}
        <div className="text-sm text-muted-foreground">
          <span className="text-foreground font-medium">{formatBytes(response.size)}</span>
        </div>

        <div className="flex-1" />

        {/* Copy Button */}
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 px-2 py-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          {copied ? (
            <>
              <Check className="w-4 h-4 text-green-500" />
              Copied
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              Copy
            </>
          )}
        </button>
      </div>

      {/* Section Tabs */}
      <div className="flex items-center border-b border-border">
        {(['body', 'headers'] as const).map((section) => (
          <button
            key={section}
            onClick={() => setActiveSection(section)}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 transition-colors capitalize',
              activeSection === section
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            {section}
            {section === 'headers' && (
              <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-muted rounded">
                {Object.keys(response.headers).length}
              </span>
            )}
          </button>
        ))}

        {activeSection === 'body' && (
          <div className="flex-1 flex justify-end px-3">
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={prettyPrint}
                onChange={(e) => setPrettyPrint(e.target.checked)}
                className="w-4 h-4 rounded"
              />
              Pretty Print
            </label>
          </div>
        )}
      </div>

      {/* Section Content */}
      <div className="flex-1 overflow-auto min-h-0 flex flex-col">
        {activeSection === 'body' && (
          <div className="flex-1 min-h-0">
            {formattedBody ? (
              <Editor
                height="100%"
                language={responseLanguage}
                value={formattedBody}
                theme={monacoTheme}
                options={{
                  readOnly: true,
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  fontSize: 13,
                  lineNumbers: 'on',
                  wordWrap: 'on',
                  folding: true,
                  automaticLayout: true,
                  renderLineHighlight: 'none',
                  scrollbar: {
                    verticalScrollbarSize: 10,
                    horizontalScrollbarSize: 10,
                  },
                }}
              />
            ) : (
              <div className="p-3 text-sm text-muted-foreground">Empty response</div>
            )}
          </div>
        )}

        {activeSection === 'headers' && (
          <div className="p-3 space-y-1">
            {Object.entries(response.headers).map(([key, value]) => (
              <div key={key} className="flex gap-2 text-sm">
                <span className="font-medium text-foreground">{key}:</span>
                <span className="text-muted-foreground break-all">{value}</span>
              </div>
            ))}
            {Object.keys(response.headers).length === 0 && (
              <p className="text-muted-foreground">No headers</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
